/**
 * The intake package, on the Documents page.
 *
 * Sources was its own tab: the email a deal arrived on, the originals stored
 * with it, and the field mapping read out of them. All three are documents, so
 * they belong beside the deal's other documents rather than behind a separate
 * tab a reader has to know about.
 *
 * The stored originals also appear as rows in the document table below, so a
 * reader looking for a file finds it in the one list they already search and
 * filter. The provenance that separates them is carried on the row rather than
 * lost to the merge: an intake original is labelled as one, states that it
 * arrived rather than naming an uploader, and shows no version — see
 * `DocumentOrigin` in `documentsModel.ts`.
 *
 * What this panel adds over those rows is the package itself: the envelope the
 * deal arrived on, and the field mapping read out of the originals. Neither is
 * a document, so neither belongs in the table.
 *
 * Preview is not merely preserved, it is the better of the two viewers: a
 * stored original opens in `SourceViewerDialog`, which renders the email, the
 * PDF (text or original), and each sheet of a workbook in place, and can
 * highlight the exact cell a value was read from. A vault document opens as a
 * blob in a new tab. Nothing here loses a preview by moving — and a row in the
 * table opens the same viewer.
 */

import { Eye, Mail, Paperclip } from 'lucide-react'

import type { DealFieldCatalogue } from '@/services/dealFile'
import type { SourceFileRef, ingestionService } from '@/services/ingestion'
import { formatTimestamp } from '@/utils/dates'

import { intakeMappingRows } from './intakeMapping'

type IntakePackage = Awaited<ReturnType<typeof ingestionService.getById>> | undefined

export function IntakePackagePanel({
  sourceIngestionId,
  sourceFiles,
  pkg,
  loading,
  onOpen,
  laterFileCount = 0,
  catalogue,
}: {
  sourceIngestionId?: string | null
  sourceFiles: SourceFileRef[]
  pkg: IntakePackage
  loading: boolean
  onOpen: (sha: string) => void
  /** Files that arrived on later messages in the conversation. They are in
   *  the document table; this panel is the first message's package. */
  laterFileCount?: number
  /** The deal field catalogue: names each mapping row by its label. */
  catalogue?: DealFieldCatalogue
}) {
  if (!sourceIngestionId) {
    return (
      <p className="px-1 py-3 text-[10px] text-stone-500">
        No linked intake package — this deal was created manually or before source linkage.
        Documents uploaded to the deal are listed below.
      </p>
    )
  }
  if (loading) {
    return (
      <p className="px-1 py-3 text-[10px] text-stone-500" aria-busy="true">
        Loading the intake package…
      </p>
    )
  }

  const envelope = pkg?.emailEnvelope
  const mapping = pkg?.fieldMapping ?? {}
  const mappingEntries = intakeMappingRows(mapping, catalogue)

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <section className="shrink-0">
        <h4 className="mb-1 font-mono text-[8px] uppercase tracking-widest text-stone-600">
          Arrived on
        </h4>
        {envelope ? (
          <dl className="grid grid-cols-[54px_1fr] gap-y-0.5 text-[10px]">
            <dt className="text-stone-600">From</dt>
            <dd className="min-w-0 break-words text-stone-300">
              {envelope.fromName
                ? `${envelope.fromName}${envelope.fromEmail ? ` <${envelope.fromEmail}>` : ''}`
                : envelope.fromEmail ?? '—'}
            </dd>
            <dt className="text-stone-600">Subject</dt>
            <dd className="min-w-0 break-words text-stone-300">
              {envelope.subject ?? pkg?.sourceSubject ?? '—'}
            </dd>
            <dt className="text-stone-600">Sent</dt>
            {/* A desk time, never the raw ISO string (nd-072). */}
            <dd className="text-stone-300">{formatTimestamp(envelope.sentAt ?? pkg?.receivedAt) || '—'}</dd>
          </dl>
        ) : (
          <p className="text-[10px] text-stone-500">
            {pkg?.sourceSubject
              ? `Subject: ${pkg.sourceSubject}`
              : 'No email envelope on this package.'}
          </p>
        )}
      </section>

      <section className="shrink-0">
        <div className="mb-1 flex items-center justify-between gap-2">
          <h4 className="font-mono text-[8px] uppercase tracking-widest text-stone-600">
            Originals on this message
            {sourceFiles.length > 0 ? (
              <span className="ml-1 text-stone-700">{sourceFiles.length}</span>
            ) : null}
          </h4>
          {sourceFiles.length > 0 ? (
            <button
              type="button"
              onClick={() =>
                onOpen(
                  sourceFiles.find((file) => file.kind === 'email_source')?.sha256
                    ?? sourceFiles[0].sha256,
                )
              }
              className="flex items-center gap-1 text-[9px] text-amber-300 hover:text-amber-200"
            >
              <Eye size={10} /> Open all
            </button>
          ) : null}
        </div>
        {sourceFiles.length === 0 ? (
          <p className="text-[10px] text-stone-500">
            No stored originals. Re-upload the package on Intake if they were lost.
          </p>
        ) : (
          <ul className="space-y-0.5">
            {sourceFiles.map((file) => (
              <li key={file.sha256}>
                <button
                  type="button"
                  onClick={() => onOpen(file.sha256)}
                  className="flex w-full items-center gap-1.5 rounded px-1 py-1 text-left text-[10px] text-stone-300 hover:bg-white/[0.04] hover:text-amber-200"
                >
                  {file.kind === 'email_source' ? (
                    <Mail size={10} className="shrink-0 text-blue-400" />
                  ) : (
                    <Paperclip size={10} className="shrink-0 text-stone-500" />
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    {file.kind === 'email_source'
                      ? `Original email — ${file.filename}`
                      : file.filename}
                  </span>
                  <Eye size={10} className="shrink-0 text-stone-600" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {laterFileCount > 0 ? (
          <p className="mt-1 text-[9px] leading-tight text-stone-400">
            {laterFileCount} more file{laterFileCount === 1 ? '' : 's'} arrived on later messages in this
            conversation — listed in the document table.
          </p>
        ) : null}
        <p className="mt-1 text-[8.5px] leading-tight text-stone-600">
          Immutable evidence of what arrived. These are listed in the document table below
          alongside the files uploaded to the deal, which can be revised.
        </p>
      </section>

      {mappingEntries.length > 0 ? (
        <section className="flex min-h-[120px] flex-1 flex-col">
          <h4 className="mb-1 font-mono text-[8px] uppercase tracking-widest text-stone-600">
            Field mapping
            <span className="ml-1 text-stone-700" data-testid="mapping-count">{mappingEntries.length}</span>
          </h4>
          <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1 text-[10px] text-stone-400">
            {mappingEntries.map((row) => (
              <li key={row.label} className="flex gap-2" data-testid="mapping-row">
                <span className="min-w-0 flex-1 break-words text-stone-300">{row.label}</span>
                <span className="min-w-0 flex-1 break-words text-stone-500">
                  {row.sources.join('; ') || '—'}
                </span>
              </li>
            ))}
          </ul>

        </section>
      ) : null}
    </div>
  )
}

export default IntakePackagePanel
